/**
 * PublishSuccessPrompt — shown after an activity is successfully published.
 * Encourages the producer to share the activity immediately.
 * 
 * Designed as a celebratory moment that funnels into ShareStudioLite.
 */

import { PartyPopper, Share2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  FarmlySheet,
  FarmlySheetHeader,
  FarmlySheetBody,
} from '@/components/ui/FarmlySheet';
import { SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';

interface PublishSuccessPromptProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventTitle: string;
  eventDate?: string | null;
  onShare: () => void;
}

function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return format(new Date(iso), "EEEE d MMMM", { locale: sv });
  } catch {
    return '';
  }
}

export function PublishSuccessPrompt({
  open,
  onOpenChange,
  eventTitle,
  eventDate,
  onShare,
}: PublishSuccessPromptProps) {
  const dateText = formatDateShort(eventDate);

  return (
    <FarmlySheet open={open} onOpenChange={onOpenChange}>
      <FarmlySheetHeader>
        <SheetTitle className="sr-only">Aktivitet publicerad</SheetTitle>
        <SheetDescription className="sr-only">Din aktivitet har publicerats framgångsrikt</SheetDescription>
      </FarmlySheetHeader>

      <FarmlySheetBody className="flex flex-col items-center text-center px-6 py-8 gap-5">
        {/* Celebration */}
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
          <PartyPopper className="h-8 w-8 text-primary" />
        </div>

        <div className="space-y-2">
          <h2 className="text-lg font-bold text-foreground">
            Din aktivitet är publicerad 🎉
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-[280px]">
            <span className="font-medium text-foreground">{eventTitle}</span>
            {dateText && (
              <span className="block mt-0.5 capitalize">{dateText}</span>
            )}
          </p>
        </div>

        <p className="text-sm text-muted-foreground">
          Dela den med dina gäster – det tar bara 10 sekunder.
        </p>

        {/* Actions */}
        <div className="flex flex-col gap-2.5 w-full max-w-[280px]">
          <Button
            size="lg"
            className="w-full gap-2"
            onClick={() => {
              onOpenChange(false);
              // Small delay so the sheet closes before the next one opens
              setTimeout(onShare, 200);
            }}
            data-testid="publish-success-share-btn"
          >
            <Share2 className="h-4 w-4" />
            Dela aktiviteten
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={() => onOpenChange(false)}
            data-testid="publish-success-close-btn"
          >
            Stäng
          </Button>
        </div>
      </FarmlySheetBody>
    </FarmlySheet>
  );
}
